// ========================================
// Resentment Item Component
// ========================================
// Individual resentment card with status management

import { useState } from 'react';
import { format } from 'date-fns';
import { useResentments } from '../../hooks/useResentments';
import { Resentment, ResentmentStatus } from '../../types';

interface ResentmentItemProps {
  resentment: Resentment;
}

const ResentmentItem = ({ resentment }: ResentmentItemProps) => {
  const { editResentment, removeResentment } = useResentments();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    target: resentment.target,
    reason: resentment.reason,
    notes: resentment.notes || '',
  });

  const formattedDate = resentment.dateAdded
    ? format(
        typeof resentment.dateAdded === 'object' && 'toDate' in resentment.dateAdded
          ? resentment.dateAdded.toDate()
          : new Date(resentment.dateAdded),
        'MMM d, yyyy'
      )
    : '';

  const releasedDate =
    resentment.dateReleased && resentment.status === 'released'
      ? format(
          typeof resentment.dateReleased === 'object' && 'toDate' in resentment.dateReleased
            ? resentment.dateReleased.toDate()
            : new Date(resentment.dateReleased),
          'MMM d, yyyy'
        )
      : '';

  const handleSave = async () => {
    try {
      await editResentment(resentment.id!, {
        target: formData.target,
        reason: formData.reason,
        notes: formData.notes || undefined,
      });
      setIsEditing(false);
    } catch (err) {
      console.error('Error editing resentment:', err);
    }
  };

  const handleChangeStatus = async (newStatus: ResentmentStatus) => {
    try {
      await editResentment(resentment.id!, { status: newStatus });
    } catch (err) {
      console.error('Error updating resentment status:', err);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this resentment?')) {
      try {
        await removeResentment(resentment.id!);
      } catch (err) {
        console.error('Error deleting resentment:', err);
      }
    }
  };

  const statusColors: Record<ResentmentStatus, string> = {
    active: 'rgba(239, 68, 68, 0.2)',
    processing: 'rgba(245, 158, 11, 0.2)',
    released: 'rgba(34, 197, 94, 0.2)',
  };

  if (isEditing) {
    return (
      <div className="item-card glass-card editing">
        <div className="form-group">
          <label>Who or what</label>
          <input
            type="text"
            value={formData.target}
            onChange={(e) => setFormData({ ...formData, target: e.target.value })}
            className="glass-input"
          />
        </div>
        <div className="form-group">
          <label>Reason</label>
          <textarea
            value={formData.reason}
            onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
            className="glass-input"
            rows={3}
          />
        </div>
        <div className="form-group">
          <label>Notes</label>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            placeholder="Notes (optional)"
            className="glass-input"
            rows={2}
          />
        </div>
        <div className="item-actions">
          <button onClick={() => setIsEditing(false)} className="btn-ghost">
            Cancel
          </button>
          <button onClick={handleSave} className="glass-button">
            Save
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`item-card glass-card status-${resentment.status}`}
      style={{ borderLeftColor: statusColors[resentment.status] }}
    >
      <div className="item-header">
        <span className="status-badge" style={{ backgroundColor: statusColors[resentment.status] }}>
          {resentment.status}
        </span>
      </div>

      <div className="item-content">
        <h3 className="item-title">{resentment.target}</h3>
        <p className="item-reason">{resentment.reason}</p>
        <p className="item-affected">Affected: {resentment.affectedArea}</p>
        {resentment.notes && <p className="item-notes">{resentment.notes}</p>}
        <p className="item-date">
          Added {formattedDate}
          {releasedDate && ` • Released ${releasedDate}`}
        </p>
      </div>

      <div className="item-actions">
        <button onClick={() => setIsEditing(true)} className="btn-ghost" title="Edit">
          ✏️
        </button>

        {resentment.status !== 'released' && (
          <select
            value={resentment.status}
            onChange={(e) => handleChangeStatus(e.target.value as ResentmentStatus)}
            className="status-select"
            title="Change status"
          >
            <option value="active">Active</option>
            <option value="processing">Processing</option>
            <option value="released">Released</option>
          </select>
        )}

        <button onClick={handleDelete} className="btn-ghost" title="Delete">
          🗑️
        </button>
      </div>
    </div>
  );
};

export default ResentmentItem;
